<script setup>
const emit = defineEmits(['refresh'])
const props = defineProps({
  item: { type: Object, required: true },
  showSelect: { type: Boolean, required: false, default: false },
  disableSelect: { type: Boolean, required: false, default: false },
  selectLabel: { type: String, required: false, default: '' },
  redirectProfessionalToCockpitAfterInterest: { type: Boolean, default: false },
})

const user = useStrapiUser()
const { sort } = useTagSort()
const { upload } = useUploadUtils()
const selected = defineModel('selected', { type: Boolean, default: false })
const showMoreTags = ref(false)

const isProfessional = computed(() => user.value?.type === 'professional')

const tags = computed(() => {
  let tags = [...(props.item?.tags || [])]
  tags = tags.sort((a, b) => a.order - b.order)
  tags = sort(tags)
  return tags
})

const matchAverage = computed(() =>
  props.item.matchingscore_total
    ? props.item.matchingscore_total
    : props.item?.matchings?.find((match) => match.type === 'total')?.score,
)

const matchPersonality = computed(() =>
  props.item.matchingscore_personality
    ? props.item.matchingscore_personality
    : props.item?.matchings?.find((match) => match.type === 'personality')
        ?.score,
)

const matchCulture = computed(() =>
  props.item.matchingscore_culture
    ? props.item.matchingscore_culture
    : props.item?.matchings?.find((match) => match.type === 'culture')?.score,
)

const matchCognitive = computed(() =>
  props.item.matchingscore_cognitive
    ? props.item.matchingscore_cognitive
    : props.item?.matchings?.find((match) => match.type === 'cognitive')?.score,
)

const matchMotivations = computed(() =>
  props.item.matchingscore_motivations
    ? props.item.matchingscore_motivations
    : props.item?.matchings?.find((match) => match.type === 'motivations')
        ?.score,
)

const image = computed(() => props.item?.media?.items?.[0])
const logo = computed(() => props.item?.company?.logo)

function afterInterest() {
  if (
    props.redirectProfessionalToCockpitAfterInterest &&
    isProfessional.value
  ) {
    navigateTo({ name: 'cockpit', query: { expandContainer: 'interested_in' } })
  }
}
</script>

<template>
  <div class="flex flex-col justify-stretch lg:mb-0">
    <div class="flex flex-col">
      <div class="relative flex shrink-0">
        <NuxtLink v-if="logo?.url" :to="`/vacancies/${item.slug}`">
          <div
            class="absolute bottom-[24px] left-[24px] z-10 h-[80px] w-[80px] overflow-hidden rounded-md bg-white"
          >
            <img
              :src="upload(logo, 'thumbail')"
              class="h-full w-full object-contain"
            />
          </div>
        </NuxtLink>
        <figure
          v-if="image?.url"
          class="mask-card relative z-[5] flex h-[208px] w-full max-w-full overflow-hidden"
        >
          <BaseVisual
            :file="image"
            class="w-full bg-white"
            size="small"
            cover
            width="164px"
            height="194px"
            type="image"
          />
        </figure>
        <div
          v-else
          class="mask-card relative flex h-[208px] w-full bg-primary"
        ></div>
        <ClientOnly>
          <component
            :is="resolveComponent('VeProgress')"
            v-if="isProfessional"
            class="absolute right-[15px] top-[7px] mt-2 lg:h-[80px] lg:w-[80px] md:h-[135px] md:w-[135px] h-[75px] w-[75px]"
            :progress="
              matchAverage && matchAverage !== null
                ? Math.round(matchAverage)
                : 0
            "
            :no-data="false"
            :determinate="false"
            color="#9EFF28"
            empty-color="#E7EDF3"
            color-fill="#4A154B"
            :size="
              $viewport.isGreaterOrEquals('desktop')
                ? 80
                : $viewport.isGreaterOrEquals('tablet')
                  ? 135
                  : 75
            "
            :thickness="4"
            :empty-thickness="2"
            animation="bounce 1500 500"
            font-size="0.9rem"
            :legend-formatter="
              ({ currentValue }) => `${matchAverage ? currentValue : '-'}%`
            "
            font-color="#FFFFFF"
            dot="0"
          />
        </ClientOnly>
      </div>
    </div>
    <div
      class="box-content flex basis-full flex-col justify-between rounded-md rounded-t-none border border-t-0 border-solid border-gray-400 bg-white p-6"
    >
      <div>
        <NuxtLink :to="`/vacancies/${item.slug}`">
          <h3
            class="text-2xl mb-4 break-words font-sans font-semibold text-secondary"
          >
            {{ item?.title }}
          </h3>
        </NuxtLink>

        <div class="flex justify-between">
          <div>
            <NuxtLink :to="`/companies/${item?.company?.slug}`">
              <p class="text-md text-gray-800">
                {{ item?.company?.name }}
              </p>
            </NuxtLink>
            <p v-if="item?.company?.address" class="mb-8 text-sm text-gray-600">
              {{ item?.company?.address?.address }},
              {{ item?.company?.address?.city }}
            </p>
          </div>
        </div>

        <div
          v-if="isProfessional"
          class="mb-4 grid xl:grid-cols-2 lg:grid-cols-1 sm:grid-cols-2 grid-cols-1 gap-2 lg:mb-6"
        >
          <AssessmentMatch
            v-if="matchPersonality !== null"
            :match="matchPersonality"
            title="Persoonlijkheid"
          />
          <AssessmentMatch
            v-if="matchCulture !== null"
            :match="matchCulture"
            title="Cultuur"
          />
          <AssessmentMatch
            v-if="matchMotivations !== null"
            :match="matchMotivations"
            title="Drijfveren"
          />
          <AssessmentMatch
            v-if="matchCognitive !== null"
            :match="matchCognitive"
            title="Cognitief"
          />
        </div>

        <div class="mb-6 flex flex-wrap items-center">
          <div>
            <Tag
              v-for="(tag, tagKey) in tags.slice(0, 5)"
              :key="tagKey"
              :text="tag.name"
              :margin-bottom="true"
              theme="outlined"
            />
          </div>
          <HeightTransition>
            <div v-if="showMoreTags" class="inline">
              <Tag
                v-for="(tag, tagKey) in tags.slice(5)"
                :key="tagKey"
                :text="tag.name"
                :margin-bottom="true"
                theme="outlined"
              />
            </div>
          </HeightTransition>
        </div>
        <Button
          v-if="item.tags?.length > 5"
          theme="tertiary"
          class="mb-4 h-10"
          @click.stop="showMoreTags = !showMoreTags"
        >
          <template #content>
            <p v-if="showMoreTags">Toon minder tags</p>
            <p v-else>Toon meer tags</p>
          </template>
          <template #icon-right>
            <IconChevronDown
              class="ml-4 h-3 w-3 transition-transform"
              :class="{ 'rotate-180': showMoreTags }"
            />
          </template>
        </Button>
      </div>

      <div class="self-stretch">
        <ButtonInterest
          v-if="isProfessional && item?.id"
          :target-type="isProfessional ? 'vacancy' : 'professional'"
          :target-id="item?.id"
          class="mb-2 w-full"
          @interaction-created="afterInterest"
        />

        <Button
          v-if="!user"
          text="interesse tonen"
          theme="accent"
          class="mb-2 w-full"
          :path="`/login?redirectAfterLogin=/companies/${item.slug}`"
        />

        <Button
          theme="primary"
          :text="'Bekijk vacature'"
          :path="`/vacancies/${item.slug}`"
          size="md"
          class="mb-2 w-full"
        />

        <ButtonDenied
          v-if="isProfessional && item?.id"
          :target-type="isProfessional ? 'vacancy' : 'professional'"
          :target-id="item?.id"
          not-interested
          text="Geen interesse"
          class="w-full bg-transparent hover:bg-transparent hover:text-secondary hover:underline"
          @refresh="emit('refresh')"
        />

        <CompareCheckbox
          v-if="showSelect"
          v-model="selected"
          :disabled="disableSelect"
          :label="'Vergelijk deze vacature met andere'"
          :item-id="item.id"
        />
      </div>
    </div>
  </div>
</template>
